import { useEffect, useState, lazy, Suspense } from "react";
import api from "@/lib/api";
import Header from "@/components/site/Header";
import Hero from "@/components/site/Hero";
import TrustLogos from "@/components/site/TrustLogos";
import ServicesGrid from "@/components/site/ServicesGrid";
import CaseStudies from "@/components/site/CaseStudies";
import Features from "@/components/site/Features";
import FAQ from "@/components/site/FAQ";
import Footer from "@/components/site/Footer";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import { useI18n } from "@/lib/i18n";

// Non-LCP heavy widgets — load only after the page is interactive (or via idle).
const MultiStepLeadForm = lazy(() => import("@/components/site/MultiStepLeadForm"));
const ChatbotWidget = lazy(() => import("@/components/site/ChatbotWidget"));

// Minimal fallback content so the landing page renders something — even before
// the API responds — and NEVER shows a "reduced view" warning to visitors.
const FALLBACK_CONTENT = {
    hero: {},
    logos: [],
    services: [],
    cases: [],
    features: [],
    faq: [],
};
const FALLBACK_SETTINGS = {
    whatsapp_number: "4915231656312",
    whatsapp_prefill: "",
    phone_number: "+4915231656312",
    contact_email: "kontakt@bamediaperformance.com",
};

export default function Landing() {
    const [content, setContent] = useState(null);
    const [settings, setSettings] = useState(null);
    const { t } = useI18n();

    useEffect(() => {
        let cancelled = false;
        let loaded = false; // local flag — NOT React state, avoids stale closure
        const safeGet = (url, fallback) =>
            api.get(url).then((r) => r.data).catch(() => fallback);

        // Hard fallback — guarantees the page renders even if the API hangs.
        // Uses a local `loaded` flag so it can't overwrite a successful response.
        const timeout = setTimeout(() => {
            if (!cancelled && !loaded) {
                setContent(FALLBACK_CONTENT);
                setSettings(FALLBACK_SETTINGS);
            }
        }, 4000);

        (async () => {
            const [c, s] = await Promise.all([
                safeGet("/content", FALLBACK_CONTENT),
                safeGet("/settings/public", FALLBACK_SETTINGS),
            ]);
            if (cancelled) return;
            loaded = true;
            setContent(c || FALLBACK_CONTENT);
            setSettings(s || FALLBACK_SETTINGS);
        })();

        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!content) {
        return (
            <div className="min-h-screen bg-[#0F172A] flex items-center justify-center" aria-hidden="true">
                <div className="size-10 rounded-full bg-[#10B981] animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8FAFC]">
            <Header />
            <main>
                <Hero content={content} settings={settings} />
                <TrustLogos logos={content.logos || []} />
                <ServicesGrid services={content.services || []} />
                <CaseStudies cases={content.cases || []} />
                <Features features={content.features || []} />

                {/* Lead form / Kontakt */}
                <section id="kontakt" data-testid="contact-section" className="bg-[#F8FAFC] py-24">
                    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                        <div className="lg:col-span-5">
                            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#10B981]">
                                {t("sections.cta_title")}
                            </div>
                            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
                                {t("sections.cta_sub")}
                            </h2>
                            <p className="mt-5 text-[#64748B] text-base leading-relaxed">
                                3 Schritte. Keine Verpflichtung. Wir analysieren dein Business und schicken dir einen konkreten Vorschlag.
                            </p>
                            <ul className="mt-6 space-y-2 text-sm text-[#0F172A]/85">
                                <li>· Branche & Bedarf prüfen</li>
                                <li>· Kontaktdaten hinterlassen</li>
                                <li>· Innerhalb 24h melden wir uns</li>
                            </ul>
                        </div>
                        <div className="lg:col-span-7">
                            <Suspense fallback={<div className="rounded-2xl border border-[#E2E8F0] bg-white h-[500px] animate-pulse" />}>
                                <MultiStepLeadForm />
                            </Suspense>
                        </div>
                    </div>
                </section>

                <FAQ faqs={content.faq || []} />
            </main>
            <Footer content={content} settings={settings} />
            <MobileStickyCTA />
            <DeferredChatbot />
            <div className="bm-mobile-spacer md:hidden" />
        </div>
    );
}

/**
 * Loads ChatbotWidget after the page becomes idle (requestIdleCallback)
 * OR after 3 seconds of inactivity OR on first user scroll/touch.
 * Saves ~25 KB on initial JS execution and keeps TBT down.
 */
function DeferredChatbot() {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (ready) return;
        let cancelled = false;
        const mount = () => { if (!cancelled) setReady(true); };

        // 1) idle callback if available
        const idleId = window.requestIdleCallback
            ? window.requestIdleCallback(mount, { timeout: 3000 })
            : null;
        // 2) hard fallback 3s timer
        const timer = setTimeout(mount, 3000);
        // 3) earliest user interaction
        const interactionHandler = () => { mount(); };
        window.addEventListener("scroll", interactionHandler, { once: true, passive: true });
        window.addEventListener("pointerdown", interactionHandler, { once: true, passive: true });

        return () => {
            cancelled = true;
            clearTimeout(timer);
            if (idleId && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
            window.removeEventListener("scroll", interactionHandler);
            window.removeEventListener("pointerdown", interactionHandler);
        };
    }, [ready]);

    if (!ready) return null;
    return (
        <Suspense fallback={null}>
            <ChatbotWidget />
        </Suspense>
    );
}
